import React, { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Heart, MessageCircle, Sparkles, Trash2, Star } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { timeAgo } from '../utils/timeAgo';
import MentionInput from './MentionInput';
import { renderMentions } from '../utils/renderMentions';

const PostCard = ({ post, onUpdate, onDelete }) => {
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentMentions, setCommentMentions] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  const handleLike = async () => {
    try {
      await api.post(`/posts/${post.id}/like`);
      onUpdate();
    } catch (error) {
      toast.error('Failed to like post');
    }
  };

  const handleCelebrate = async () => {
    try {
      await api.post(`/posts/${post.id}/celebrate`);
      onUpdate();
    } catch (error) {
      toast.error('Failed to celebrate post');
    }
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;

    setLoading(true);
    try {
      await api.post(`/posts/${post.id}/comment`, {
        content: commentText,
        mentions: commentMentions,
      });
      setCommentText('');
      setCommentMentions([]);
      onUpdate();
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this post?')) return;

    try {
      await api.delete(`/posts/${post.id}`);
      toast.success('Post deleted');
      onDelete(post.id);
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleFeature = async () => {
    try {
      await api.post(`/admin/posts/${post.id}/feature`);
      toast.success(post.is_featured ? 'Post unfeatured' : 'Post featured in Success Stories!');
      onUpdate();
    } catch (error) {
      toast.error('Failed to feature post');
    }
  };

  const isLiked = post.likes?.includes(user?.id);
  const isCelebrated = post.celebrates?.includes(user?.id);
  const isAuthor = post.author_id === user?.id;
  const isAdmin = user?.is_admin;

  const getPostTypeBadge = () => {
    switch (post.type) {
      case 'win':
        return (
          <div className="flex items-center gap-2">
            <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
              🏆 WIN
            </Badge>
            {post.is_featured && (
              <Badge className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-white">
                ⭐ Featured
              </Badge>
            )}
          </div>
        );
      case 'question':
        return (
          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
            ❓ QUESTION
          </Badge>
        );
      default:
        return (
          <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">
            UPDATE
          </Badge>
        );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="post-card"
    >
      <Card className={`bg-white border border-slate-100 p-5 shadow-sm hover:shadow-md transition-all ${post.type === 'win' ? 'border-l-4 border-l-green-500' : ''}`}>
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-12 w-12">
              <AvatarImage src={post.author_photo} />
              <AvatarFallback className="bg-[#1E3A8A] text-white">
                {getInitials(post.author_name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold text-slate-900">{post.author_name}</p>
              <p className="text-xs text-slate-500">
                GBA Alumni • {timeAgo(post.created_at)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {getPostTypeBadge()}
            {isAdmin && post.type === 'win' && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleFeature}
                className={`h-8 w-8 ${post.is_featured ? 'text-yellow-600' : 'text-slate-400 hover:text-yellow-600'}`}
                data-testid="feature-post-button"
                title={post.is_featured ? 'Unfeature from Success Stories' : 'Feature in Success Stories'}
              >
                <Star className={`h-4 w-4 ${post.is_featured ? 'fill-current' : ''}`} />
              </Button>
            )}
            {(isAuthor || isAdmin) && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDelete}
                className="h-8 w-8 text-slate-400 hover:text-red-600"
                data-testid="delete-post-button"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="mb-4">
          <p className="text-slate-700 whitespace-pre-wrap">
            {renderMentions(post.content, post.mentions)}
          </p>
          {post.image_url && (
            <img
              src={post.image_url}
              alt="Post"
              className="mt-3 rounded-md max-w-full h-auto block"
            />
          )}
          {post.instagram_url && (
            <div className="mt-3 max-w-md mx-auto rounded-md overflow-hidden border border-slate-200 bg-slate-50">
              <iframe
                src={`https://www.instagram.com/${post.instagram_kind === 'reel' ? 'reel' : 'p'}/${post.instagram_url.split('/').filter(Boolean).pop()}/embed/`}
                title="Instagram post"
                className="w-full"
                style={{ height: '620px' }}
                frameBorder="0"
                scrolling="no"
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                allowFullScreen
                data-testid="feed-instagram-embed"
              />
            </div>
          )}
        </div>

        {/* Interaction Bar */}
        <div className="flex items-center justify-between pt-4 mt-4 border-t border-slate-50">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLike}
              className={`gap-2 ${isLiked ? 'text-red-600' : 'text-slate-600'}`}
              data-testid="like-button"
            >
              <Heart className={`h-4 w-4 ${isLiked ? 'fill-current' : ''}`} />
              <span>{post.likes?.length || 0}</span>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleCelebrate}
              className={`gap-2 ${isCelebrated ? 'text-green-600' : 'text-slate-600'}`}
              data-testid="celebrate-button"
            >
              <Sparkles className={`h-4 w-4 ${isCelebrated ? 'fill-current' : ''}`} />
              <span>{post.celebrates?.length || 0}</span>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowComments(!showComments)}
              className="gap-2 text-slate-600"
              data-testid="comment-toggle-button"
            >
              <MessageCircle className="h-4 w-4" />
              <span>{post.comments?.length || 0}</span>
            </Button>
          </div>
        </div>

        {/* Comments Section */}
        {showComments && (
          <div className="mt-4 pt-4 border-t border-slate-100" data-testid="comments-section">
            <div className="space-y-3 mb-3">
              {post.comments?.map((comment) => (
                <div key={comment.id} className="flex gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={comment.user_photo} />
                    <AvatarFallback className="bg-slate-200 text-slate-600 text-xs">
                      {getInitials(comment.user_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 bg-slate-50 rounded-md p-2">
                    <p className="text-sm font-semibold text-slate-900">{comment.user_name}</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">
                      {renderMentions(comment.content, comment.mentions)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <div className="flex-1">
                <MentionInput
                  value={commentText}
                  onChange={setCommentText}
                  mentions={commentMentions}
                  onMentionsChange={setCommentMentions}
                  placeholder="Write a comment... type @ to mention"
                  minHeight="60px"
                  testId="comment-input"
                />
              </div>
              <Button
                onClick={handleComment}
                disabled={loading || !commentText.trim()}
                className="bg-[#1E3A8A] hover:bg-[#172554]"
                data-testid="comment-submit-button"
              >
                Post
              </Button>
            </div>
          </div>
        )}
      </Card>
    </motion.div>
  );
};

export default PostCard;
